import type { FC, ReactNode } from "react";
import { useEffect, useState } from "react";
import { useController, type Control, type FieldPathByValue, type FieldValues } from "react-hook-form";
import { RadioGroup, FormControl, FormControlLabel, FormLabel } from "@mui/material";
import { useTranslation } from "@privacy/translations";
import { Radio } from "@privacy/ui";
import type {
  DocumentFile,
  DocumentSourceKind,
  DocumentSourcePayload,
  SavedDocumentSource,
} from "../dynamicForm/types";
import { ConfirmDeleteDialog } from "./ConfirmDeleteDialog";
import { AnimatedHeight } from "./AnimatedHeight";

export type DocumentSourceFormValues = { kind: DocumentSourceKind; url: string; file: DocumentFile | null };

export const toDocumentSourceFormValues = (source: SavedDocumentSource): DocumentSourceFormValues => {
  if (source.kind !== "file") return { kind: "link", url: source.url ?? "", file: null };
  const { fileId, fileName, size } = source;
  return { kind: "file", url: "", file: { fileId, fileName, size } };
};

export const toDocumentSourcePayload = ({ kind, url, file }: DocumentSourceFormValues): DocumentSourcePayload => {
  if (kind === "link") return { kind, url: url.trim() };
  if (!file) throw new Error("A file must be uploaded before the document is saved");
  return { kind, fileId: file.fileId };
};

type DocumentSourceRadioGroupProps = {
  value: DocumentSourceKind;
  onChange: (value: DocumentSourceKind) => void;
  savedFileName?: string;
  disabled?: boolean;
};

const DocumentSourceRadioGroup: FC<DocumentSourceRadioGroupProps> = ({ value, onChange, savedFileName, disabled }) => {
  const { t } = useTranslation();
  const [isConfirmingSwitchToLink, setIsConfirmingSwitchToLink] = useState(false);
  const options = [
    { value: "link", label: t("Document.Source.Link") },
    { value: "file", label: t("Document.Source.File") },
  ] as const;

  const handleChange = (source: DocumentSourceKind) => {
    if (source === "link" && savedFileName) {
      setIsConfirmingSwitchToLink(true);
      return;
    }
    onChange(source);
  };

  return (
    <>
      <FormControl component="fieldset" disabled={disabled}>
        <FormLabel
          component="legend"
          sx={theme => ({
            "fontSize": theme.typography.pxToRem(14),
            "lineHeight": 1.5,
            "pb": 1,
            "fontWeight": "600",
            "color": theme.palette.text.primary,
            "&.Mui-focused": { color: theme.palette.text.primary },
          })}
        >
          {t("Document.Source.Label")}
        </FormLabel>
        <RadioGroup row value={value} onChange={(_, kind) => handleChange(kind === "file" ? "file" : "link")}>
          {options.map(option => (
            <FormControlLabel
              key={option.value}
              slotProps={{ typography: { sx: { fontSize: theme => theme.typography.pxToRem(14) } } }}
              value={option.value}
              control={<Radio size="small" sx={{ py: 1, px: 2 }} />}
              label={option.label}
            />
          ))}
        </RadioGroup>
      </FormControl>
      {isConfirmingSwitchToLink && savedFileName && (
        <ConfirmDeleteDialog
          isOpen
          onClose={() => setIsConfirmingSwitchToLink(false)}
          onDelete={async () => onChange("link")}
          headerText={t("Document.SwitchToLink.ConfirmHeader")}
          message={t("Document.SwitchToLink.ConfirmMessage", { fileName: savedFileName })}
          confirmButtonLabel={t("Document.SwitchToLink.ConfirmButton")}
        />
      )}
    </>
  );
};

const useIgnoreFileDropOutsideDropArea = () => {
  useEffect(() => {
    const ignoreFileDropOutsideDropArea = (event: DragEvent) => {
      if (event.defaultPrevented) return;
      if (!event.dataTransfer?.types.includes("Files")) return;
      event.preventDefault();
      if (event.type === "dragover") event.dataTransfer.dropEffect = "none";
    };
    window.addEventListener("dragover", ignoreFileDropOutsideDropArea);
    window.addEventListener("drop", ignoreFileDropOutsideDropArea);
    return () => {
      window.removeEventListener("dragover", ignoreFileDropOutsideDropArea);
      window.removeEventListener("drop", ignoreFileDropOutsideDropArea);
    };
  }, []);
};

type DocumentSourceFieldsProps<T extends FieldValues> = Pick<
  DocumentSourceRadioGroupProps,
  "savedFileName" | "disabled"
> & {
  control: Control<T>;
  name: FieldPathByValue<T, DocumentSourceKind>;
  onKindChange?: (kind: DocumentSourceKind) => void;
  linkField: ReactNode;
  fileField?: ReactNode;
};

export const DocumentSourceFields = <T extends FieldValues>({
  control,
  name,
  onKindChange,
  linkField,
  fileField,
  ...radioGroupProps
}: DocumentSourceFieldsProps<T>) => {
  const { field } = useController({ control, name });
  const kind: DocumentSourceKind = field.value;
  useIgnoreFileDropOutsideDropArea();

  const handleChange = (value: DocumentSourceKind) => {
    onKindChange?.(value);
    field.onChange(value);
  };

  return (
    <>
      {fileField && <DocumentSourceRadioGroup value={kind} onChange={handleChange} {...radioGroupProps} />}
      <AnimatedHeight>{kind === "link" ? linkField : fileField}</AnimatedHeight>
    </>
  );
};
